import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Badge, Button, Icon } from 'antd'
import ReferenceSearch from './ReferenceSearch'
import { COLORS } from './utils'

const PANEL_WIDTH = 260
const GRID_COLUMNS = 2
const GRID_GAP = 8
const GRID_PADDING = 10
const HEADER_HEIGHT = 30
const GROUP_GAP = 10
const DEFAULT_VIEWPORT_HEIGHT = 440
const OVERSCAN_ROWS = 1
const SCROLL_IDLE_MS = 140
const SEARCH_DEBOUNCE_MS = 120

const getGroupMeta = image => {
  if (image.sceneTitle === 'Uploaded') {
    return { key: 'uploaded', title: 'Uploaded', kind: 'uploaded' }
  }
  if (image.sceneTitle === 'Location') {
    return { key: 'locations', title: 'Locations', kind: 'locations' }
  }
  if (image.sceneTitle === 'Prop') {
    return { key: 'props', title: 'Props', kind: 'props' }
  }
  if (image.sceneTitle === 'Character') {
    return { key: 'characters', title: 'Characters', kind: 'characters' }
  }

  const sceneTitle = image.sceneTitle || 'Scene'

  return {
    key: `scene:${sceneTitle}`,
    title: sceneTitle,
    kind: 'scene'
  }
}

const getGroupIcon = kind => {
  if (kind === 'locations') return 'environment'
  if (kind === 'characters') return 'user'
  if (kind === 'props') return 'appstore'
  if (kind === 'uploaded') return 'cloud-upload'
  return 'picture'
}

const findFirstVisibleRowIndex = (rows, visibleStart) => {
  let low = 0
  let high = rows.length

  while (low < high) {
    const mid = Math.floor((low + high) / 2)
    const row = rows[mid]

    if (row.top + row.height < visibleStart) {
      low = mid + 1
    } else {
      high = mid
    }
  }

  return low
}

const ReferenceImage = memo(
  ({
    image,
    selected,
    deferImage,
    disabled,
    cachedLoaded,
    onSelect,
    onImageLoaded,
    onAnnotate
  }) => {
    const [loaded, setLoaded] = useState(cachedLoaded)

    useEffect(() => {
      setLoaded(cachedLoaded)
    }, [cachedLoaded, image.url])

    const shouldRenderImage = loaded || !deferImage

    return (
      <div
        className="reference-image-item"
        style={{
          position: 'relative',
          borderRadius: 6,
          overflow: 'hidden',
          border: `2px solid ${selected ? COLORS.primary : 'transparent'}`,
          background: COLORS.background,
          opacity: disabled ? 0.58 : 1
        }}
      >
        <button
          type="button"
          onClick={() => onSelect(image.url)}
          disabled={disabled}
          style={{
            display: 'block',
            width: '100%',
            padding: 0,
            border: 0,
            background: 'transparent',
            cursor: disabled ? 'not-allowed' : 'pointer'
          }}
        >
          <div
            className={`reference-image-thumbnail${
              loaded ? ' reference-image-thumbnail--loaded' : ''
            }`}
            style={{
              width: '100%',
              aspectRatio: '1/1',
              background: COLORS.background
            }}
          >
            {shouldRenderImage ? (
              <img
                src={image.url}
                alt={image.shotTitle || 'Reference'}
                loading="lazy"
                decoding="async"
                onLoad={() => {
                  setLoaded(true)
                  onImageLoaded(image.url)
                }}
                onError={() => {
                  setLoaded(true)
                  onImageLoaded(image.url)
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  opacity: loaded ? 1 : 0,
                  transition: 'opacity 0.12s ease-out'
                }}
              />
            ) : null}
          </div>
        </button>

        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '16px 5px 4px',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.68))',
            color: '#fff',
            fontSize: 9,
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            pointerEvents: 'none'
          }}
        >
          {image.shotTitle || 'Image'}
        </div>

        {selected ? (
          <>
            <div
              style={{
                position: 'absolute',
                top: 4,
                left: 4,
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: COLORS.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                pointerEvents: 'none'
              }}
            >
              <Icon type="check" style={{ color: '#fff', fontSize: 10 }} />
            </div>
            <Button
              size="small"
              shape="circle"
              icon="highlight"
              aria-label="Annotate selected reference"
              disabled={disabled}
              onClick={event => {
                event.stopPropagation()
                onAnnotate(image.url)
              }}
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 22,
                height: 22,
                minWidth: 22,
                padding: 0,
                background: 'rgba(255,255,255,0.94)',
                border: 'none',
                boxShadow: '0 2px 4px rgba(0,0,0,0.14)'
              }}
            />
          </>
        ) : null}
      </div>
    )
  }
)

const ReferencePanel = memo(
  ({
    references,
    currentImage,
    selectedReferences,
    onChange,
    onAnnotate,
    disabled = false
  }) => {
    const viewportRef = useRef(null)
    const scrollFrameRef = useRef(null)
    const scrollIdleTimerRef = useRef(null)
    const isScrollingRef = useRef(false)
    const loadedImageUrlsRef = useRef(new Set())
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
    const [viewportWidth, setViewportWidth] = useState(0)
    const [viewportHeight, setViewportHeight] = useState(0)
    const [scrollTop, setScrollTop] = useState(0)
    const [isScrolling, setIsScrolling] = useState(false)

    useEffect(() => {
      const timerId = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm)
      }, SEARCH_DEBOUNCE_MS)

      return () => clearTimeout(timerId)
    }, [searchTerm])

    useEffect(() => {
      isScrollingRef.current = true
      setIsScrolling(true)
      if (scrollIdleTimerRef.current) {
        clearTimeout(scrollIdleTimerRef.current)
      }
      scrollIdleTimerRef.current = setTimeout(() => {
        isScrollingRef.current = false
        setIsScrolling(false)
      }, SCROLL_IDLE_MS)

      const viewport = viewportRef.current
      if (!viewport) return undefined

      const updateSize = () => {
        setViewportWidth(width =>
          width === viewport.clientWidth ? width : viewport.clientWidth
        )
        setViewportHeight(height =>
          height === viewport.clientHeight ? height : viewport.clientHeight
        )
      }

      updateSize()

      if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver(updateSize)
        resizeObserver.observe(viewport)
        return () => resizeObserver.disconnect()
      }

      window.addEventListener('resize', updateSize)
      return () => window.removeEventListener('resize', updateSize)
    }, [])

    useEffect(
      () => () => {
        if (scrollFrameRef.current) {
          cancelAnimationFrame(scrollFrameRef.current)
        }
        if (scrollIdleTimerRef.current) {
          clearTimeout(scrollIdleTimerRef.current)
        }
      },
      []
    )

    useEffect(() => {
      setScrollTop(0)
      if (viewportRef.current) viewportRef.current.scrollTop = 0
    }, [debouncedSearchTerm])

    const selectedSet = useMemo(
      () => new Set(selectedReferences || []),
      [selectedReferences]
    )

    const handleSelect = useCallback(
      url => {
        if (disabled) return

        const currentReferences = Array.isArray(selectedReferences)
          ? selectedReferences
          : []

        const nextReferences = currentReferences.includes(url)
          ? currentReferences.filter(referenceUrl => referenceUrl !== url)
          : [...currentReferences, url]

        onChange(nextReferences)
      },
      [disabled, onChange, selectedReferences]
    )

    const handleClear = useCallback(() => {
      if (!disabled) onChange([])
    }, [disabled, onChange])

    const handleImageLoaded = useCallback(url => {
      loadedImageUrlsRef.current.add(url)
    }, [])

    const handleScroll = useCallback(event => {
      const nextScrollTop = event.currentTarget.scrollTop

      if (!isScrollingRef.current) {
        isScrollingRef.current = true
        setIsScrolling(true)
      }

      if (scrollIdleTimerRef.current) {
        clearTimeout(scrollIdleTimerRef.current)
      }

      scrollIdleTimerRef.current = setTimeout(() => {
        isScrollingRef.current = false
        setIsScrolling(false)
      }, SCROLL_IDLE_MS)

      if (scrollFrameRef.current) {
        cancelAnimationFrame(scrollFrameRef.current)
      }

      scrollFrameRef.current = requestAnimationFrame(() => {
        setScrollTop(nextScrollTop)
        scrollFrameRef.current = null
      })
    }, [])

    const groups = useMemo(() => {
      const normalizedSearchTerm = debouncedSearchTerm.trim().toLowerCase()
      const seen = new Set()
      const groupMap = new Map()

      ;(references || []).forEach(image => {
        if (!image || !image.url || image.url === currentImage) return
        if (seen.has(image.url)) return

        const groupMeta = getGroupMeta(image)

        const label = `${image.shotTitle || ''} ${image.sceneTitle || ''} ${
          groupMeta.title || ''
        }`
          .toLowerCase()
          .trim()

        if (normalizedSearchTerm && !label.includes(normalizedSearchTerm)) {
          return
        }

        seen.add(image.url)

        if (!groupMap.has(groupMeta.key)) {
          groupMap.set(groupMeta.key, {
            ...groupMeta,
            images: []
          })
        }

        groupMap.get(groupMeta.key).images.push(image)
      })

      return Array.from(groupMap.values()).filter(
        group => group.images.length > 0
      )
    }, [currentImage, debouncedSearchTerm, references])

    const totalCount = groups.reduce(
      (count, group) => count + group.images.length,
      0
    )

    const effectiveWidth = viewportWidth || PANEL_WIDTH
    const cellSize = Math.max(
      1,
      (effectiveWidth - GRID_PADDING * 2 - GRID_GAP * (GRID_COLUMNS - 1)) /
        GRID_COLUMNS
    )
    const rowPitch = cellSize + GRID_GAP

    const { contentHeight, rows } = useMemo(() => {
      if (groups.length === 0) {
        return { contentHeight: 0, rows: [] }
      }

      const nextRows = []
      let nextTop = GRID_PADDING

      groups.forEach(group => {
        nextRows.push({
          type: 'header',
          key: `${group.key}-header`,
          top: nextTop,
          height: HEADER_HEIGHT,
          group
        })
        nextTop += HEADER_HEIGHT

        const rowCount = Math.ceil(group.images.length / GRID_COLUMNS)

        for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
          const startIndex = rowIndex * GRID_COLUMNS

          nextRows.push({
            type: 'images',
            key: `${group.key}-row-${rowIndex}`,
            top: nextTop,
            height: cellSize,
            images: group.images.slice(startIndex, startIndex + GRID_COLUMNS)
          })
          nextTop += rowPitch
        }

        if (rowCount > 0) nextTop -= GRID_GAP
        nextTop += GROUP_GAP
      })

      return {
        contentHeight: nextTop + GRID_PADDING - GROUP_GAP,
        rows: nextRows
      }
    }, [cellSize, groups, rowPitch])

    const visibleRows = useMemo(() => {
      if (rows.length === 0) return []

      const measuredViewportHeight = viewportHeight || DEFAULT_VIEWPORT_HEIGHT
      const overscanDistance = rowPitch * OVERSCAN_ROWS
      const visibleStart = Math.max(0, scrollTop - overscanDistance)
      const visibleEnd = scrollTop + measuredViewportHeight + overscanDistance
      const firstIndex = findFirstVisibleRowIndex(rows, visibleStart)
      const nextVisibleRows = []

      for (let index = firstIndex; index < rows.length; index++) {
        const row = rows[index]
        if (row.top > visibleEnd) break
        nextVisibleRows.push(row)
      }

      return nextVisibleRows
    }, [rowPitch, rows, scrollTop, viewportHeight])

    return (
      <div
        style={{
          width: PANEL_WIDTH,
          borderLeft: `1px solid ${COLORS.borderLight}`,
          background: COLORS.white,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          minHeight: 0
        }}
      >
        <div
          style={{
            height: 48,
            padding: '0 12px',
            borderBottom: `1px solid ${COLORS.borderLight}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            flexShrink: 0
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 0
            }}
          >
            <Icon type="picture" style={{ color: COLORS.textMuted }} />
            <span
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: COLORS.text,
                whiteSpace: 'nowrap'
              }}
            >
              References
            </span>
            <Badge
              count={selectedReferences.length}
              showZero
              style={{ backgroundColor: COLORS.primary }}
            />
          </div>

          <Button
            size="small"
            onClick={handleClear}
            disabled={disabled || selectedReferences.length === 0}
            style={{ flexShrink: 0 }}
          >
            Clear
          </Button>
        </div>

        <div
          style={{
            padding: 10,
            borderBottom: `1px solid ${COLORS.borderLight}`,
            flexShrink: 0
          }}
        >
          <ReferenceSearch value={searchTerm} onChange={setSearchTerm} />
        </div>

        <div
          ref={viewportRef}
          onScroll={handleScroll}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain'
          }}
        >
          {totalCount === 0 ? (
            <div
              style={{
                padding: 20,
                textAlign: 'center',
                color: COLORS.textMuted,
                fontSize: 12
              }}
            >
              {debouncedSearchTerm ? 'No matches' : 'No references'}
            </div>
          ) : (
            <div style={{ position: 'relative', height: contentHeight }}>
              {visibleRows.map(row =>
                row.type === 'header' ? (
                  <div
                    key={row.key}
                    style={{
                      position: 'absolute',
                      top: row.top,
                      left: GRID_PADDING,
                      right: GRID_PADDING,
                      height: row.height,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '0 4px',
                      fontSize: 11,
                      fontWeight: 500,
                      color: COLORS.textSecondary
                    }}
                  >
                    <Icon type={getGroupIcon(row.group.kind)} />
                    <span>{row.group.title}</span>
                    <span style={{ color: COLORS.textMuted }}>
                      ({row.group.images.length})
                    </span>
                  </div>
                ) : (
                  <div
                    key={row.key}
                    style={{
                      position: 'absolute',
                      top: row.top,
                      left: GRID_PADDING,
                      right: GRID_PADDING,
                      height: row.height,
                      display: 'grid',
                      gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
                      gap: GRID_GAP
                    }}
                  >
                    {row.images.map(image => (
                      <ReferenceImage
                        key={image.url}
                        image={image}
                        selected={selectedSet.has(image.url)}
                        deferImage={isScrolling}
                        disabled={disabled}
                        cachedLoaded={loadedImageUrlsRef.current.has(
                          image.url
                        )}
                        onSelect={handleSelect}
                        onImageLoaded={handleImageLoaded}
                        onAnnotate={onAnnotate}
                      />
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    )
  }
)

export default ReferencePanel
